import { fetchWithCORSProxy } from '@elfsight/lib-utils';
import { TIMEOUT } from '../../constants';

export interface CreateMetadataFetcherProps {
  url: string;
  parser: (response: Response) => Promise<string | undefined>;
  headers?: HeadersInit;
  widgetToken?: string;
  forceCORSProxy?: boolean;
  abortController?: AbortController;
  withTimeout?: boolean;
}

export type FetchMetadataProps = Omit<
  CreateMetadataFetcherProps,
  'parser' | 'headers'
>;
type MetadataFetcherResult = {
  shouldUseCORSProxy: boolean;
  data?: string;
};
export type MetadataFetcher = (
  props: FetchMetadataProps
) => Promise<MetadataFetcherResult | undefined>;

export const createMetadataFetcher = async ({
  url,
  parser,
  headers,
  widgetToken,
  forceCORSProxy,
  abortController,
  withTimeout
}: CreateMetadataFetcherProps) => {
  let shouldUseCORSProxy = false;
  const signal = (() => {
    const signals = [];

    if (abortController) {
      signals.push(abortController.signal);
    }

    if (withTimeout) {
      signals.push(AbortSignal.timeout(TIMEOUT));
    }

    return AbortSignal.any(signals);
  })();
  const derivedFetch = () => {
    if (forceCORSProxy) {
      return fetchWithCORSProxy(url, widgetToken, {
        signal,
        headers
      });
    }

    return fetch(url, { signal, headers }).catch(() => {
      shouldUseCORSProxy = true;

      return fetchWithCORSProxy(url, widgetToken, {
        signal,
        headers
      });
    });
  };
  const response = await derivedFetch();
  if (!response.ok) {
    return;
  }

  return {
    data: await parser(response),
    shouldUseCORSProxy
  };
};
